/* eslint-disable indent */
import moment from 'moment-timezone';
import {
    Asset, AssetGroup,
    Booking,
    Depot,
    DetailedIssueReport,
    IssueReport,
    Location,
    LocationBookingStats,
    Thing,
} from './types';

export type ServerData = any;

export const processBookingData = (serverData: ServerData): Booking => {
    const order: Booking = serverData;

    return order;
};

export const processMultipleBookingsData = (serverData: ServerData): Booking[] => {
    return Array.isArray(serverData) ? serverData : [serverData];
};

export const processLocationData = (serverData: ServerData): Location => {
    return {
        id: serverData.id,
        enabled: serverData.enabled,
        name: serverData.name,
        address: serverData.address,
        status: serverData.status,
        services: serverData.services,
        // #3219: `time_zone`, camel-cased by the backend's processPGData.
        timeZone: serverData.timeZone,
    };
};

export const processAssetData = (serverData: ServerData): Asset => {
    return {
        id: serverData.id,
        name: serverData.name,
        accessories: serverData.accessories,
    };
};

export const processDepotData = (serverData: ServerData): Depot => {
    return {
        enabled: serverData.enabled,
        status: serverData?.status?.bookable,
        id: serverData.id,
        locationId: serverData.locationId,
        thingsId: serverData.thingsId,
        name: serverData.name || 'N/A',
        description: serverData.description,
        doorMapping: serverData.doorMapping,
        doorCount: serverData.doorCount,
        // status: serverData.status,
        last_seen: serverData.last_seen
            ? moment.tz(serverData.last_seen, 'UTC').toDate()
            : null,
        // connectionStatus,
        // phoneNumber: serverData.phoneNumber || null,
        containerType: serverData.containerType,
        isFictional: serverData.isFictional || false,
        // rawData: serverData,
    };
};
export const processIssueReportData = (serverData: ServerData): IssueReport => {
    // Some problem reports (e.g. payment/booking-type issues without a
    // specific physical asset) come back with an empty `assets` array.
    // Reading `assets[0].xxx` unconditionally throws and aborts the whole
    // `.map(processIssueReportData)` call, surfacing as a misleading
    // `snackbar.error.fetch_issueReports` to the user. Read defensively.
    const firstAsset = (serverData.assets && serverData.assets[0]) || {};
    return {
        id: serverData.id,
        depotId: firstAsset.depotId || '',
        locationId: serverData.locationId,
        door: serverData.door,
        doorSize: firstAsset.doorSize || '',
        address: firstAsset.address || '',
        orderId: serverData.orderId,
        bookingNumber: serverData.bookingNumber,
        message: serverData.message,
        // A migrated JSON column: shape-checked, a `{}` is no comments.
        comments: Array.isArray(serverData.comments) ? serverData.comments : null,
        issueType: serverData.issueType,
        state: serverData.state,
        // The SUP equipment-check lifecycle and its flagged items. The server
        // has sent both since #1604; dropping them here hid the card's
        // customer-status chip. `items` is a migrated JSON column, so it is
        // shape-checked rather than defaulted.
        status: serverData.status ?? null,
        items: Array.isArray(serverData.items) ? serverData.items : null,
        createdBy: serverData.createdBy || undefined,
        createdAt: serverData.createdAt,
        profileInfo: serverData.profileInfo,
        service: serverData.service,
        userCreatorEmail: serverData.userCreatorEmail || '',
        userCreatorName: serverData.userCreatorName || '',
        archivedAt: serverData.archivedAt ?? null,
        archivedBy: serverData.archivedBy ?? null,
        archiveReason: serverData.archiveReason ?? null,
        archiveNote: serverData.archiveNote ?? null,
    };
};

export const processDetailedIssueReportData = (
    serverData: ServerData,
): DetailedIssueReport => {
    return {
        id: serverData.id,
        message: serverData.message,
        orderId: serverData.orderId,
        state: serverData.state,
        createdBy: serverData.createdBy || undefined,
        createdAt: serverData.createdAt || undefined,
        door: serverData.door,
        assets: serverData.assets,
    };
};

export const processThingsData = (serverData: ServerData): Thing => {
    const lastSeen = serverData.lastSeenDate
        ? moment.tz(serverData.lastSeenDate, 'UTC').toDate()
        : null;
    const minutesFromLastSeen = lastSeen
        ? Math.round((new Date().getTime() - lastSeen.getTime()) / 60000)
        : undefined;
    // const connectionStatus = lastSeen
    //     ? minutesFromLastSeen! < 30 ? 'available' : 'unavailable'
    //     : 'unknown';
    return {
        id: serverData.id,
        name: serverData.name,
        lastSeenDate: lastSeen,
        minutesFromLastSeen: minutesFromLastSeen,
        remoteItState: serverData.remoteItState,
    };
};

export const processLocationBookingStats = (
    serverData: ServerData,
): LocationBookingStats => {
    return {
        daily: Object.keys(serverData)
            .map((dateString) => ({
                date: moment(dateString, 'DD.MM.YYYY').toDate(),
                orderCount: Number(serverData[dateString].orderCount),
                resourceCount: Number(serverData[dateString].resourceCount),
                total: Number(serverData[dateString].total),
            }))
            .sort((a, b) => a.date.getTime() - b.date.getTime()),
    };
};


export const processAssetGroupData = (assetGroupData: any): AssetGroup => {
    return {
        id: assetGroupData.id,
        name: assetGroupData.name,
        description: assetGroupData.description,
        locationId: assetGroupData.locationId,
        availabilities: assetGroupData.availibility,
        isFullDay: assetGroupData.isFullDay,
    };
};

export const processAssetGroupsData = (assetGroupsData: any[]): AssetGroup[] => {
    return assetGroupsData.map((assetGroupData: any): AssetGroup => processAssetGroupData(assetGroupData));
};
